export interface CardImageUris {
  small?: string;
  normal?: string;
  large?: string;
  png?: string;
  art_crop?: string;
  border_crop?: string;
}

export interface CardImages {
  front: CardImageUris | null;
  back?: CardImageUris | null; // For double-faced cards
}

/** The full set of card zones a Player owns. The single source of truth for
 * "which pile" across battlefield drop targets, hotkeys, and pile viewers. */
export type PileType = 'deck' | 'exile' | 'discard' | 'hand' | 'scry' | 'sideboard';

/**
 * Zones whose contents opponents can see. A card's identity may appear in the
 * shared action log only if it is visible in the zone it came from or the one it
 * lands in — see `Player.movePileCard`.
 */
export const PUBLIC_PILES: ReadonlySet<PileType> = new Set<PileType>(['exile', 'discard']);

export interface Card {
  id: string;
  cardNumber: number; // Persistent numbering for stack tracking
  name?: string; // Actual card name from Scryfall
  type_line?: string; // Card type from Scryfall (e.g., "Basic Land — Mountain", "Creature — Human Wizard")
  oracleText?: string; // Official card text
  images?: CardImages; // Image URIs from Scryfall
  scryfallId?: string; // Scryfall ID for future API calls
  commander?: boolean; // Imported under a commander header — auto-drawn into the opening hand
  /**
   * Whether the import tagged this as a foil printing. A fact about the card the
   * player owns — written for everyone, entitlement-blind, and never changed
   * after import.
   */
  isFoil?: boolean;
  /**
   * The cosmetic foil style currently applied, e.g. `"nebula"`. This is what
   * renderers key off, and the only one of the two that peers can act on.
   *
   * It is separate from `isFoil` because the two are decided at different times
   * by different authorities. `isFoil` comes from the deck list; `foil` is the
   * owner's entitlement decision, and entitlement is local — a peer cannot
   * derive "should this shimmer" from `isFoil` alone, so the owner has to
   * serialize that answer into the shared doc. Keeping `isFoil` around is also
   * what lets a purchase apply retroactively to cards already in play.
   */
  foil?: string;
  x: number;
  y: number;
  rotation: number;
  isTapped: boolean;
  /** Tilted 45° to mark summoning sickness. Mutually exclusive with `isTapped`
   * (a card sits at exactly one of 0°/45°/90°) — see `resolveCardRotation` and
   * the `sick`/`tap` actions in `battlefieldCardActions`. Optional because it was
   * added after cards were already persisted in Yjs/IndexedDB; absent reads as
   * not-sick, which is the correct default. */
  isSick?: boolean;
  isFlipped: boolean;
  counters: number[]; // Array of counter values
}

export interface DeckMetadata {
  id: string; // Unique deck ID
  name: string; // User-provided deck name
  format?: string; // Commander, Standard, Modern, etc.
  source: 'scryfall' | 'moxfield' | 'manual';
  cardCount: number;
  importedAt: Date;
  lastModified: Date;
}

export interface SavedDeck {
  metadata: DeckMetadata;
  cards: Card[]; // Full card list with images
  /**
   * Cards imported from a sideboard/maybeboard/companion section, kept out of
   * the deck itself. Optional because decks saved before sideboards existed
   * don't carry it — treat absent as empty, never as "not yet loaded".
   */
  sideboard?: Card[];
  /**
   * The list this deck was imported from, kept verbatim so editing can reopen
   * exactly what the player last saved.
   *
   * `cards` cannot stand in for it. A `Card` records the printing it resolved
   * to but not the printing the list *asked* for — there is no set code or
   * collector number on it — so a decklist rebuilt from cards is names and
   * quantities only. Re-importing that would quietly repick every card's
   * printing, and re-run a lookup that can fail: the round trip through the
   * edit dialog would be free to lose cards a working deck already had.
   *
   * Optional because decks saved before editing existed don't carry it. Absent
   * means "rebuild from cards" — see `decklistTextForEditing`, which is the one
   * place that decides — never "this deck has no list".
   */
  decklistText?: string;
}

export interface CustomCounter {
  id: string;
  title: string;
  icon: string;
  value: number;
}

export interface PlayerState {
  id: string;
  name: string;
  health: number;
  hand: Card[];
  exilePile: Card[];
  discardPile: Card[];
  deck: Card[];
  sideboard: Card[];
  customCounters: CustomCounter[];
}

export interface PlayerConfig {
  initialHealth: number;
}